import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { LucideIcon } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '../../ui/Sidebar/Sidebar';
import { CustomTrigger } from './CustomTriggerSidebar';

export interface NavigationItem {
  title: string;
  icon: LucideIcon | React.ComponentType<{ className?: string }>;
  url: string;
}

interface AppSidebarProps {
  isCollapsed: boolean;
  navigationItems?: NavigationItem[];
  showStagingBadge?: boolean;
}

export default function AppSidebar({ isCollapsed, navigationItems = [], showStagingBadge }: AppSidebarProps) {
  const { isMobile, toggleSidebar } = useSidebar();
  const { t, i18n } = useTranslation();

  const currentYear = new Date().getFullYear();

  const handleLogoClick = () => {
    if (isCollapsed && !isMobile) {
      toggleSidebar();
    }
  };

  return (
    <Sidebar collapsible="icon" side={i18n.language === 'ar' ? 'right' : 'left'}>
      {/* Header */}
      <SidebarHeader className="border-b h-[80px]">
        {!isCollapsed || isMobile ? (
          <div className="flex items-center justify-between px-4 py-4 boder-r">
            <img
              src="/images/logos/hero-Logo.svg"
              alt="HERO"
              className="h-9 cursor-pointer"
              onClick={handleLogoClick}
            />
            <CustomTrigger isCollapsed={isCollapsed} className="border-[1.5px] rounded-lg p-2" />
          </div>
        ) : (
          <div className="mt-[40%]">
            <img
              src="/images/logos/hero-logo-small.svg"
              alt="HERO"
              className="h-9 cursor-pointer"
              onClick={handleLogoClick}
            />
          </div>
        )}
      </SidebarHeader>

      {/* Content */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-[12px] font-[400] leading-[18px] text-gray-600 mt-4 px-7">
            {t('dashboard:Menu')}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <div className="px-[13px] mb-3 mt-3">
              {/* Show trigger button next to logo when expanded */}
              {isCollapsed && (
                <CustomTrigger
                  isCollapsed={isCollapsed}
                  className="border-[1.5px] rounded-lg p-1"
                />
              )}
            </div>
            <SidebarMenu>
              {navigationItems.map(item => (
                <SidebarMenuItem key={item.title} className={isCollapsed ? 'px-2' : ''}>
                  <SidebarMenuButton
                    asChild
                    tooltip={t(item.title)}
                    className="!py-5 px-7 :hover:!bg-transparent">
                    <NavLink
                      to={item.url}
                      className="side-bar-links">
                      <item.icon className="!h-6 !w-6" />
                      <span className="text-[14px] font-[500] leading-[22px]">{t(item.title)}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className={isCollapsed ? 'hidden' : 'block'}>
        <div className="border-t h-[80px] text-center">
          <p className="pt-6 text-gray-600 body2">
            {t('dashboard:footer.copyright', { year: currentYear })}
          </p>
          <span
            className={`inline-block text-[10px] mt-1 ${
              showStagingBadge
                ? 'bg-amber-100 text-amber-700 px-2.5 py-0.5 rounded-full font-medium'
                : 'text-gray-400'
            }`}>
            v{import.meta.env.VITE_APP_VERSION || 'dev'}
          </span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
